import { Injectable } from '@angular/core';
import { CardRarity } from '@/app/shared/components/card/card.model';

interface RaritySound {
	src: string;
	/** Coupe le son à cette durée max, quelle que soit la longueur réelle du fichier. */
	maxDurationMs: number;
}

/** Un son par rareté, joué quand la carte est retournée — COMMON reste volontairement silencieuse. */
const RARITY_SOUND: Partial<Record<CardRarity, RaritySound>> = {
	RARE: { src: '/assets/sound/QUOI.mp3', maxDurationMs: 1000 },
	EPIC: { src: '/assets/sound/FDGOD.mp3', maxDurationMs: 4500 },
	LEGENDARY: { src: '/assets/sound/KING.mp3', maxDurationMs: 5000 },
	COLLECTOR: { src: encodeURI('/assets/sound/Aux Armes.mp4'), maxDurationMs: 6000 },
};

@Injectable({ providedIn: 'root' })
export class CardSoundService {
	/** Petit clic générique joué à l'atterrissage de chaque carte pendant la cascade d'ouverture. */
	private clickAudio: HTMLAudioElement | null = null;

	private readonly rarityPool = new Map<CardRarity, HTMLAudioElement>();
	private readonly stopTimers = new Map<CardRarity, ReturnType<typeof setTimeout>>();

	constructor() {
		// Chargés dès l'instanciation du service (montage de la page contenant
		// <peps-booster-opening/>), pas à l'ouverture du booster — évite tout lag.
		for (const [rarity, sound] of Object.entries(RARITY_SOUND) as [CardRarity, RaritySound][]) {
			const audio = new Audio(sound.src);
			audio.preload = 'auto';
			audio.volume = 0.35;
			this.rarityPool.set(rarity, audio);
		}
	}

	/** Clic de révélation générique, joué à l'atterrissage de chaque carte dans la cascade. */
	playCardReveal(): void {
		try {
			if (!this.clickAudio) {
				this.clickAudio = new Audio('/assets/sound/card_reveal.mp3');
				this.clickAudio.preload = 'auto';
			}
			this.clickAudio.currentTime = 0;
			this.clickAudio.volume = 0.05;
			this.clickAudio.play().catch(() => { /* autoplay bloqué */ });
		} catch { /* Audio indisponible */ }
	}

	/** Son de rareté joué quand la carte est retournée (silence pour COMMON). */
	playRarityReveal(rarity: CardRarity): void {
		const audio = this.rarityPool.get(rarity);
		const sound = RARITY_SOUND[rarity];
		if (!audio || !sound) return;
		try {
			const existingTimer = this.stopTimers.get(rarity);
			if (existingTimer) clearTimeout(existingTimer);

			audio.currentTime = 0;
			audio.play().catch(() => { /* autoplay bloqué */ });

			this.stopTimers.set(rarity, setTimeout(() => {
				audio.pause();
				audio.currentTime = 0;
			}, sound.maxDurationMs));
		} catch { /* Audio indisponible */ }
	}
}
